import { useEffect, useMemo, useRef, useState } from "react"

import { DatePicker, Dropdown, Flex, Select, Typography } from "antd"
import cls from "classname"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import Icon from "../../../Icon"
import { calendarLocale, langMapISO } from "../../../Utils/utils"
import ButtonComponent from "../../Button"
import { COMPARE_IDS, COMPARE_LABELS, getAvailableCompareIds, getCompareRange } from "./presets"

import styles from "./CompareSelect.module.less"

const { RangePicker } = DatePicker
const { Text } = Typography

const ISO = "YYYY-MM-DD"
const DATE_FORMAT = "MMM D, YYYY"
const toISO = (d) => (d ? d.format(ISO) : null)

export default function CompareSelect({
  currentValue,
  value,
  onChange,
  disabled,
  className,
  overlayClassName,
  placement,
  onOpenChange
}) {
  const calendarRef = useRef(null)
  const { t } = useTranslation()
  const lang = useSelector((s) => s?.auth?.userInfo?.language) || "en"
  const isMobile = useSelector((s) => s.nav.isMobile)
  const isDisabledTransition = useSelector((s) => s.nav.disabledTransition)

  const [visible, setVisible] = useState(false)
  const [draft, setDraft] = useState(value || null)
  const [isApply, setIsApply] = useState(false)

  useEffect(() => {
    if (visible) {
      setDraft(value || null)
      setIsApply(false)
    }
  }, [visible])

  const availableIds = useMemo(() => getAvailableCompareIds(currentValue), [currentValue])

  const isOn = !!value
  const isCustomActive = draft?.mode === COMPARE_IDS.CUSTOM

  const presetLabel = useMemo(() => {
    if (!value) return null
    if (value.mode === COMPARE_IDS.CUSTOM) {
      const [min, max] = value.range || []
      if (min && max) {
        const fmtMin = dayjs(min).locale(langMapISO[lang]).format(DATE_FORMAT)
        const fmtMax = dayjs(max).locale(langMapISO[lang]).format(DATE_FORMAT)
        return `${fmtMin} – ${fmtMax}`
      }
      return t(COMPARE_LABELS[COMPARE_IDS.CUSTOM])
    }
    return t(COMPARE_LABELS[value.mode])
  }, [value, lang, t])

  const handleVisibleChange = (next) => {
    if (next) setIsApply(false)
    if (!next && !isApply) setDraft(value || null)
    setVisible(next)
    onOpenChange?.(next)
  }

  const applyValue = (next) => {
    setIsApply(true)
    setDraft(next)
    onChange?.(next)
    setVisible(false)
    onOpenChange?.(false)
  }

  const handlePresetClick = (id) => {
    if (id === COMPARE_IDS.NONE) {
      applyValue(null)
      return
    }
    if (id === COMPARE_IDS.CUSTOM) {
      setDraft((prev) => ({
        mode: COMPARE_IDS.CUSTOM,
        range: prev?.mode === COMPARE_IDS.CUSTOM ? prev.range : [null, null]
      }))
      return
    }
    const range = getCompareRange(currentValue, id)
    applyValue({ mode: id, range })
  }

  const handleClearIcon = (e) => {
    if (!isOn) return // OFF: даємо клік пройти і відкрити дропдаун
    e.stopPropagation()
    onChange?.(null)
  }

  const handleCalendarChange = (dates) => {
    if (!dates || !dates.some(Boolean)) {
      setDraft({ mode: COMPARE_IDS.CUSTOM, range: [null, null] })
      return
    }
    setDraft({
      mode: COMPARE_IDS.CUSTOM,
      range: [toISO(dates[0]), toISO(dates[1])]
    })
  }

  const handleClear = () => setDraft(null)
  const handleCancel = () => {
    setDraft(value || null)
    setVisible(false)
    onOpenChange?.(false)
  }
  const handleApply = () => applyValue(draft)

  const useSimpleMobile = isMobile

  const handlePresetSelectChange = (mode) => {
    if (mode === COMPARE_IDS.CUSTOM) {
      setDraft((prev) => ({
        mode: COMPARE_IDS.CUSTOM,
        range: prev?.mode === COMPARE_IDS.CUSTOM ? prev.range : [null, null]
      }))
      return
    }
    if (mode === COMPARE_IDS.NONE) {
      applyValue(null)
      return
    }
    const range = getCompareRange(currentValue, mode)
    applyValue({ mode, range })
  }

  const presetSelectOptions = useMemo(
    () => availableIds.map((id) => ({ value: id, label: t(COMPARE_LABELS[id]) })),
    [availableIds, t]
  )

  const isApplyDisabled =
    !draft || (draft.mode === COMPARE_IDS.CUSTOM && (!draft.range?.[0] || !draft.range?.[1]))

  const pickerValue = [
    draft?.range?.[0] ? dayjs(draft.range[0]) : null,
    draft?.range?.[1] ? dayjs(draft.range[1]) : null
  ]

  const renderPreset = (id) => (
    <li
      key={id}
      className={cls(styles.preset, { [styles.active]: draft?.mode === id })}
      onClick={() => handlePresetClick(id)}
    >
      <span>{t(COMPARE_LABELS[id])}</span>
      {draft?.mode === id && <Icon type="check_simple" className={styles.presetCheck} />}
    </li>
  )

  const renderList = () => (
    <div className={styles.compactPanel}>
      <ul className={styles.presets}>{availableIds.map(renderPreset)}</ul>
    </div>
  )

  const renderActions = () => (
    <Flex gap={12} align="center" className={styles.actions}>
      <Text type="secondary" className={styles.timezoneNote}>
        {t("Dates are in UTC")}
      </Text>
      <ButtonComponent
        className={cls("flex center", styles.button)}
        text={t(useSimpleMobile ? "Cancel" : "Clear")}
        onClick={useSimpleMobile ? handleCancel : handleClear}
      />
      <ButtonComponent
        type="primary"
        className={cls("flex center", styles.button)}
        text={t("Apply")}
        onClick={handleApply}
        disabled={isApplyDisabled}
      />
    </Flex>
  )

  const rangePresetIds = useMemo(
    () => availableIds.filter((id) => id !== COMPARE_IDS.CUSTOM),
    [availableIds]
  )

  const renderRangePresets = () => (
    <ul className={styles.rangePresets}>
      {rangePresetIds.map((id) => (
        <li key={id} className={styles.rangePreset} onClick={() => handlePresetSelectChange(id)}>
          {t(COMPARE_LABELS[id])}
        </li>
      ))}
    </ul>
  )

  const renderCustomPanel = () => (
    <div
      className={cls(styles.compactPanel, {
        [styles.panelSimple]: useSimpleMobile
      })}
    >
      {!useSimpleMobile && renderRangePresets()}
      <div
        ref={calendarRef}
        className={cls(styles.calendar, {
          [styles.calendarSimple]: useSimpleMobile
        })}
      >
        {useSimpleMobile && (
          <Select
            className={styles.presetSelect}
            value={draft?.mode}
            onChange={handlePresetSelectChange}
            options={presetSelectOptions}
            placeholder={t("Select period")}
            suffixIcon={<Icon type="chevron_down_small" />}
          />
        )}
        <RangePicker
          className={styles.rangepicker}
          value={pickerValue}
          open={true}
          format={useSimpleMobile ? "YYYY-MM-DD" : "DD MMM, YYYY"}
          placeholder={[t("Select start date"), t("Select end date")]}
          locale={calendarLocale(lang)}
          nextIcon={<Icon type="chevron_right_small" style={{ fontSize: 24 }} />}
          prevIcon={<Icon type="chevron_left_small" style={{ fontSize: 24 }} />}
          allowClear={false}
          onCalendarChange={handleCalendarChange}
          getPopupContainer={() => calendarRef.current || document.body}
          disabledDate={(current) => dayjs().endOf("day") < current}
        />
        {!isMobile && renderActions()}
      </div>
      {isMobile && renderActions()}
    </div>
  )

  const dropdownRender = () => (isCustomActive ? renderCustomPanel() : renderList())

  return (
    <div
      className={cls(styles.root, className, {
        "disabled-transition": isDisabledTransition
      })}
    >
      <Dropdown
        overlayClassName={cls(styles.overlay, overlayClassName)}
        destroyPopupOnHide={true}
        getPopupContainer={() => document.getElementById("main-layout")}
        onOpenChange={handleVisibleChange}
        dropdownRender={dropdownRender}
        trigger={["click"]}
        open={visible}
        placement={placement || (isMobile ? "bottom" : "bottomLeft")}
        disabled={disabled}
      >
        <div
          className={cls(styles.trigger, {
            [styles.open]: visible,
            [styles.active]: isOn,
            [styles.disabled]: disabled
          })}
        >
          <span
            className={styles.compareSide}
            onClick={handleClearIcon}
            role={isOn ? "button" : undefined}
            aria-label={isOn ? t("Turn off compare") : undefined}
          >
            <Icon type={"compare"} className={styles.icon} />
            <Text className={styles.label}>{t("Compare")}</Text>
          </span>
          <span className={styles.divider} />
          <span className={styles.periodSide}>
            <Text
              className={cls(styles.presetLabel, {
                [styles.placeholder]: !isOn
              })}
            >
              {isOn ? presetLabel : t("Select period")}
            </Text>
            <Icon type="chevron_down_small" className={styles.arrowIcon} />
          </span>
        </div>
      </Dropdown>
    </div>
  )
}

CompareSelect.propTypes = {
  currentValue: PropTypes.shape({
    preset: PropTypes.string,
    range: PropTypes.arrayOf(PropTypes.string)
  }),
  value: PropTypes.shape({
    mode: PropTypes.string,
    range: PropTypes.arrayOf(PropTypes.string)
  }),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  overlayClassName: PropTypes.string,
  placement: PropTypes.string,
  onOpenChange: PropTypes.func
}
